import React, { useState } from 'react';
import { X, ChevronLeft, MapPin, Star, DollarSign, Home, ExternalLink } from 'lucide-react';
import { stockPhoto } from '../../assets/stock/index.ts';

type PriceRange = 'all' | 'under-400k' | '400k-600k' | 'over-600k';

interface MockVillage {
    id: number;
    name: string;
    suburb: string;
    state: string;
    distance: number;
    rating: number;
    entryCost: number;
    ongoingFees: number;
    amenities: string[];
    unitTypes: string[];
    websiteUrl?: string;
    images: string[];
}

interface RetirementVillageSearchProps {
    onClose: () => void;
}

const RetirementVillageSearch = ({ onClose }: RetirementVillageSearchProps) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [maxDistance, setMaxDistance] = useState(10);
    const [priceRange, setPriceRange] = useState<PriceRange>('all');
    const [showResults, setShowResults] = useState(false);

    // Mock retirement village data
    const mockVillages: MockVillage[] = [
        {
            id: 1,
            name: "Sunset Gardens Retirement Village",
            suburb: "Croydon",
            state: "NSW",
            distance: 1.2,
            rating: 4.4,
            entryCost: 450000,
            ongoingFees: 1200,
            amenities: ["Swimming Pool", "Gym", "Library", "Cafe", "Garden"],
            unitTypes: ["1 Bedroom", "2 Bedroom", "3 Bedroom"],
            websiteUrl: 'https://example.org/carecompanion-demo/villages/sunset-gardens',
            images: [
                stockPhoto('1564013799919-ab600027ffc6', 300, 200),
                stockPhoto('1571896349842-33c89424de2d', 300, 200),
                stockPhoto('1582407947304-fd86f028f716', 300, 200)
            ]
        },
        {
            id: 2,
            name: "Riverside Living Community",
            suburb: "Five Dock",
            state: "NSW",
            distance: 2.1,
            rating: 4.6,
            entryCost: 650000,
            ongoingFees: 1500,
            amenities: ["Water Views", "Swimming Pool", "Gym", "Library", "Café", "Garden", "Bowling Green"],
            unitTypes: ["2 Bedroom", "3 Bedroom"],
            images: [
                stockPhoto('1564013799919-ab600027ffc6', 300, 200),
                stockPhoto('1571896349842-33c89424de2d', 300, 200),
                stockPhoto('1582407947304-fd86f028f716', 300, 200)
            ]
        },
        {
            id: 3,
            name: "Heritage Park Village",
            suburb: "Campsie",
            state: "NSW",
            distance: 3.4,
            rating: 4.1,
            entryCost: 380000,
            ongoingFees: 950,
            amenities: ["Garden", "Library", "Cafe", "Community Hall"],
            unitTypes: ["1 Bedroom", "2 Bedroom"],
            websiteUrl: 'https://example.org/carecompanion-demo/villages/heritage-park',
            images: [
                stockPhoto('1512917774080-9991f1c4c750', 300, 200),
                stockPhoto('1560448204-603b3fc33ddc', 300, 200),
                stockPhoto('1564013799919-ab600027ffc6', 300, 200)
            ]
        },
        {
            id: 4,
            name: "Mountain View Retirement",
            suburb: "Burwood",
            state: "NSW",
            distance: 4.7,
            rating: 4.3,
            entryCost: 520000,
            ongoingFees: 1300,
            amenities: ["Mountain Views", "Swimming Pool", "Gym", "Library", "Cafe", "Garden"],
            unitTypes: ["2 Bedroom", "3 Bedroom"],
            websiteUrl: 'https://example.org/carecompanion-demo/villages/mountain-view-retirement',
            images: [
                stockPhoto('1564013799919-ab600027ffc6', 300, 200),
                stockPhoto('1571896349842-33c89424de2d', 300, 200),
                stockPhoto('1582407947304-fd86f028f716', 300, 200)
            ]
        },
        {
            id: 5,
            name: "Garden Court Village",
            suburb: "Ashfield",
            state: "NSW",
            distance: 2.8,
            rating: 4.0,
            entryCost: 420000,
            ongoingFees: 1100,
            amenities: ["Garden", "Library", "Cafe", "Community Hall", "Chapel"],
            unitTypes: ["1 Bedroom", "2 Bedroom", "3 Bedroom"],
            websiteUrl: 'https://example.org/carecompanion-demo/villages/garden-court',
            images: [
                stockPhoto('1559027615-cd4628902d4a', 300, 200),
                stockPhoto('1586023492125-27b2c045efd7', 300, 200),
                stockPhoto('1560448204-e02f11c3d0e2', 300, 200)
            ]
        }
    ];

    const filteredVillages = mockVillages
        .filter(village => {
            const distanceMatch = village.distance <= maxDistance;
            let priceMatch = true;
            
            if (priceRange !== 'all') {
                switch (priceRange) {
                    case 'under-400k':
                        priceMatch = village.entryCost < 400000;
                        break;
                    case '400k-600k':
                        priceMatch = village.entryCost >= 400000 && village.entryCost <= 600000;
                        break;
                    case 'over-600k':
                        priceMatch = village.entryCost > 600000;
                        break;
                }
            }
            
            return distanceMatch && priceMatch;
        })
        .sort((a, b) => a.distance - b.distance);

    const handleSearch = () => {
        if (searchTerm.trim()) {
            setShowResults(true);
        }
    };

    const handleBackToSearch = () => {
        setShowResults(false);
        setSearchTerm('');
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-2xl font-bold text-gray-900">
                        {showResults ? `Retirement Villages near ${searchTerm}` : 'Find Retirement Villages'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X size={24} />
                    </button>
                </div>
                
                <div className="p-6">
                    {!showResults ? (
                        // Search Interface
                        <div className="space-y-6">
                            <div className="text-center">
                                <p className="text-gray-600 mb-6">Enter your suburb or area to find nearby retirement villages</p>
                            </div>
                            
                            <div className="max-w-md mx-auto space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Suburb or Area</label>
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                        placeholder="e.g., Croydon, Five Dock, Campsie..."
                                        className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Max Distance: {maxDistance}km</label>
                                    <input
                                        type="range"
                                        min="1"
                                        max="20"
                                        value={maxDistance}
                                        onChange={(e) => setMaxDistance(parseInt(e.target.value))}
                                        className="w-full"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Entry Cost Range</label>
                                    <select
                                        value={priceRange}
                                        onChange={(e) => setPriceRange(e.target.value as PriceRange)}
                                        className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="all">All Price Ranges</option>
                                        <option value="under-400k">Under $400,000</option>
                                        <option value="400k-600k">$400,000 - $600,000</option>
                                        <option value="over-600k">Over $600,000</option>
                                    </select>
                                </div>
                                
                                <button
                                    onClick={handleSearch}
                                    disabled={!searchTerm.trim()}
                                    className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed text-lg font-semibold"
                                >
                                    Search Villages
                                </button>
                            </div>

                            {/* Information Section */}
                            <div className="mt-8 p-6 bg-white rounded-lg border border-green-200">
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                                            <Home className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-green-900 mb-2">Independent Living</h3>
                                        <p className="text-green-800 mb-4">
                                            Retirement villages offer independent living with optional care services. 
                                            No aged care needs assessment required - you can move in directly and access care services as needed.
                                        </p>
                                        <div className="text-sm text-green-700">
                                            <p><strong>Key Benefits:</strong></p>
                                            <ul className="list-disc list-inside mt-2 space-y-1">
                                                <li>Independent living with community support</li>
                                                <li>Optional care services available</li>
                                                <li>Social activities and amenities</li>
                                                <li>Maintenance-free lifestyle</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        // Results Interface
                        <div className="space-y-6">
                            <div className="flex items-center justify-between">
                                <button
                                    onClick={handleBackToSearch}
                                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
                                >
                                    <ChevronLeft size={20} />
                                    Back to Search
                                </button>
                            </div>

                            {/* Results */}
                            <div className="space-y-4 max-h-96 overflow-y-auto">
                                {filteredVillages.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        No villages found matching your criteria.
                                    </div>
                                ) : (
                                    filteredVillages.map(village => (
                                        <div
                                            key={village.id}
                                            className="border border-gray-200 rounded-xl p-4 md:p-5 hover:border-blue-300 hover:shadow-md transition-all"
                                        >
                                            <div className="flex flex-col md:flex-row gap-5">
                                                <div className="shrink-0 w-full md:w-[min(100%,280px)] md:max-w-[280px]">
                                                    <div className="aspect-[4/3] rounded-xl overflow-hidden bg-slate-100 ring-1 ring-slate-200/80">
                                                        {village.images?.[0] ? (
                                                            <img
                                                                src={village.images[0]}
                                                                alt={village.name}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center text-sm text-slate-400">No photo</div>
                                                        )}
                                                    </div>
                                                    {village.images && village.images.length > 1 ? (
                                                        <div className="flex gap-1.5 mt-2">
                                                            {village.images.slice(1, 4).map((image, index) => (
                                                                <img
                                                                    key={index}
                                                                    src={image}
                                                                    alt={`${village.name} view ${index + 2}`}
                                                                    className="h-14 min-w-0 flex-1 object-cover rounded-lg border border-slate-200"
                                                                />
                                                            ))}
                                                        </div>
                                                    ) : null}
                                                </div>
                                                <div className="flex-1 min-w-0 flex flex-col lg:flex-row gap-4 lg:gap-6">
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="text-xl font-bold text-gray-900 leading-tight">{village.name}</h3>
                                                        <p className="text-gray-600 mt-0.5">{village.suburb}, {village.state}</p>
                                                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-sm text-slate-600">
                                                            <span className="inline-flex items-center gap-1.5">
                                                                <MapPin size={15} className="text-sky-600 shrink-0" aria-hidden />
                                                                {village.distance}km away
                                                            </span>
                                                            <span className="inline-flex items-center gap-1.5">
                                                                <Star size={15} className="text-amber-500 shrink-0" aria-hidden />
                                                                {village.rating}/5
                                                            </span>
                                                            <span className="inline-flex items-center gap-1.5 font-medium text-slate-800">
                                                                <DollarSign size={15} className="text-emerald-600 shrink-0" aria-hidden />
                                                                ${village.entryCost.toLocaleString()}
                                                            </span>
                                                        </div>
                                                        <div className="mt-3">
                                                            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Unit types</span>
                                                            <p className="text-sm text-gray-800 mt-0.5">{village.unitTypes.join(', ')}</p>
                                                        </div>
                                                        <div className="mt-2">
                                                            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Amenities</span>
                                                            <p className="text-sm text-gray-800 mt-0.5">{village.amenities.join(', ')}</p>
                                                        </div>
                                                        <div className="mt-2">
                                                            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Monthly fees</span>
                                                            <p className="text-sm text-gray-800 mt-0.5">${village.ongoingFees.toLocaleString()}</p>
                                                        </div>
                                                        {village.websiteUrl ? (
                                                            <a
                                                                href={village.websiteUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800"
                                                            >
                                                                <ExternalLink size={14} aria-hidden />
                                                                Visit website
                                                                <span className="text-xs font-normal text-gray-500">(demo URL)</span>
                                                            </a>
                                                        ) : null}
                                                    </div>
                                                    <div className="shrink-0 flex lg:flex-col justify-end lg:justify-center gap-2 lg:w-44">
                                                        <button
                                                            type="button"
                                                            onClick={() => window.open('https://www.retirementvillages.com.au', '_blank')}
                                                            className="w-full lg:w-auto px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all font-semibold shadow-sm"
                                                        >
                                                            Contact Village
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RetirementVillageSearch;
